import React from 'react';
import { Link } from 'react-router-dom';

// Charts for the Dashboard's 30-day ticket series ([{ date, day, count }]). Styles are inline with
// colors passed in, so the PDF report can render the same markup outside the app's stylesheet.

const HEIGHT = 112;
const AXIS_DAYS = [0, 7, 14, 21, 29];

const dayLabel = (day) => day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function Axis({ data, colors }) {
  return (
    <div style={{ position: 'relative', height: 18, marginTop: 4, fontSize: 12, lineHeight: '18px', color: colors.muted }} aria-hidden="true">
      {AXIS_DAYS.filter((i) => i < data.length).map((i) => {
        const left = `${((i + 0.5) / data.length) * 100}%`;
        const edge = i === data.length - 1 ? 'translateX(-100%)' : i === 0 ? 'none' : 'translateX(-50%)';
        return (
          <span key={i} style={{ position: 'absolute', left: i === 0 ? 0 : left, transform: edge, whiteSpace: 'nowrap' }}>
            {dayLabel(data[i].day)}
          </span>
        );
      })}
    </div>
  );
}

function Scale({ max, colors }) {
  return (
    <span style={{ position: 'absolute', top: -2, right: '100%', marginRight: 6, fontSize: 12, lineHeight: '14px', color: colors.muted }} aria-hidden="true">
      {max}
    </span>
  );
}

const frame = (colors) => ({
  position: 'relative',
  height: HEIGHT,
  marginLeft: 22,
  borderBottom: `1px solid ${colors.rule}`,
  backgroundImage: `linear-gradient(${colors.rule}, ${colors.rule})`,
  backgroundSize: '100% 1px',
  backgroundRepeat: 'no-repeat',
});

/**
 * One bar per day. With linkFor(point), days that have tickets link to that path
 * (the Tickets list filtered by created date).
 */
export function DailyBars({ data, colors, linkFor, label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      <div style={frame(colors)}>
        <Scale max={max} colors={colors} />
        <ol aria-label={label} style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: '100%', margin: 0, padding: 0, listStyle: 'none' }}>
          {data.map((d) => {
            const text = `${dayLabel(d.day)}: ${d.count} ${d.count === 1 ? 'ticket' : 'tickets'} opened`;
            const bar = (
              <span
                style={{
                  display: 'block',
                  width: '100%',
                  height: d.count ? `${(d.count / max) * 100}%` : 0,
                  minHeight: d.count ? 2 : 0,
                  background: colors.bar,
                  borderRadius: '2px 2px 0 0',
                }}
              />
            );
            return (
              <li key={d.date} style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                {linkFor && d.count > 0 ? (
                  <Link to={linkFor(d)} title={text} aria-label={text} className="dash-bar" style={{ display: 'flex', alignItems: 'flex-end', width: '100%', height: '100%' }}>
                    {bar}
                  </Link>
                ) : (
                  <span title={text} style={{ display: 'flex', alignItems: 'flex-end', width: '100%', height: '100%' }}>
                    {bar}
                    <span className="sr-only">{text}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <div style={{ marginLeft: 22 }}>
        <Axis data={data} colors={colors} />
      </div>
    </div>
  );
}

// Pending tickets at the end of each day, as a step line.
export function DailyLine({ data, colors, label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const n = data.length;
  const y = (count) => (1 - count / max) * 100;
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${i} ${y(d.count)} L ${i + 1} ${y(d.count)}`).join(' ');
  return (
    <div>
      <div style={frame(colors)}>
        <Scale max={max} colors={colors} />
        <svg viewBox={`0 0 ${n} 100`} preserveAspectRatio="none" width="100%" height="100%" role="img" aria-label={label} style={{ display: 'block', overflow: 'visible' }}>
          <path d={path} fill="none" stroke={colors.line} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          {data.map((d, i) => (
            <rect key={d.date} x={i} y="0" width="1" height="100" fill="transparent">
              <title>{`${dayLabel(d.day)}: ${d.count} pending`}</title>
            </rect>
          ))}
        </svg>
      </div>
      <div style={{ marginLeft: 22 }}>
        <Axis data={data} colors={colors} />
      </div>
    </div>
  );
}
